/**
 * useUpdateHotspot — Task #27
 *
 * useMutation gegen PATCH /api/v2/hotspots/{id}.
 * Body-Wrapper: { user_hotspot: { ... } } (OpenAPI-Konvention laut Schema).
 * Editierbare Felder: start_date, sell_date, watched, affiliate, tag_list.
 * Invalidiert ['hotspot', id] und ['hotspots'] nach Erfolg.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import type { paths } from '@heliumtracker/api-types'

type PatchBody =
  paths['/api/v2/hotspots/{id}']['patch']['requestBody']['content']['application/json']['user_hotspot']

/**
 * Stream H (R4): Der PATCH akzeptiert zusaetzlich die Guard-Felder aus v1.
 * `guard` ohne `guard_muted_until` setzt die Stummschaltung serverseitig auf
 * null — das ist gewolltes v1-Verhalten und darf hier nicht kompensiert werden.
 */
export type UpdateHotspotInput = PatchBody & {
  guard?: boolean | null
  guard_muted_until?: string | null
  notify_poc?: boolean | null
  ownership_type?: string | null
}

export function useUpdateHotspot(id: string | number) {
  const queryClient = useQueryClient()
  const numericId = typeof id === 'string' ? Number(id) : id
  const stringId = String(id)

  return useMutation({
    mutationFn: async (input: UpdateHotspotInput) => {
      const result = await apiClient.PATCH('/api/v2/hotspots/{id}', {
        params: { path: { id: numericId } },
        body: { user_hotspot: input },
      })
      if (result.error) throw result.error
      return result.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hotspot', stringId] })
      void queryClient.invalidateQueries({ queryKey: ['hotspots'] })
    },
  })
}
